/**
 * Fallen Edge — Trading UI client. Read-only.
 *
 * Reads /api/v1/fallen-edge/* over same-origin nginx proxy (port 8100 internal).
 * No order controls, no fabricated fields. Missing values surface as `null` and
 * are rendered as "(unavailable)" in panels.
 *
 * All endpoints are advisory-only. The engine itself enforces
 * `advisory_only: true`, `no_order_execution: true`, `no_live_trading: true`
 * on every row; the UI re-asserts the badge regardless.
 *
 * Marker strings (kept stable so smoke tests can grep dist):
 *   - "fallen-edge"
 *   - "phase-fallen-edge-ui-v1"
 */
import { apiV1Base } from "./apiConfig";

type Envelope<T> = {
  ok: boolean;
  data: T | null;
  error?: { message: string; code: string } | null;
};

export interface FallenEdgeCandidate {
  ticker?: string | null;
  company_name?: string | null;
  current_loss_pct?: number | null;
  reason_for_loss?: string | null;
  loser_type?: string | null;          // fake_loser | real_loser | sympathy_loser | structural_breakdown
  reversal_score?: number | null;       // 0-100
  short_continuation_score?: number | null;
  avoid_score?: number | null;
  best_action?: string | null;
  entry_trigger?: string | null;
  invalidation_level?: string | null;
  risk_level?: string | null;
  beginner_explanation?: string | null;
  data_quality?: string | null;         // good | limited | poor
  missing_inputs?: string[] | null;
  sector?: string | null;
  volume?: number | null;
  avg_volume?: number | null;
  spread_bps?: number | null;
  reclaimed_vwap?: boolean | null;
  catalyst_severity?: string | null;
  advisory_only?: boolean | null;
  no_order_execution?: boolean | null;
  no_live_trading?: boolean | null;
}

export interface FallenEdgePayload {
  generated_at?: string | null;
  generated_at_et?: string | null;
  run_id?: string | null;
  candidates?: FallenEdgeCandidate[];
  count?: number | null;
  // optional engine metadata
  data_quality_summary?: Record<string, number> | null;
  notes?: string | null;
}

export interface FallenEdgeHealth {
  status?: string | null;
  last_run_at?: string | null;
  last_run_at_et?: string | null;
  candidate_count?: number | null;
  message?: string | null;
}

async function feGet<T>(path: string): Promise<Envelope<T>> {
  try {
    const res = await fetch(`${apiV1Base()}${path}`, {
      headers: { accept: "application/json" },
      credentials: "include",
      cache: "no-store",
    });
    if (!res.ok) {
      return {
        ok: false,
        data: null,
        error: { message: `HTTP ${res.status}`, code: String(res.status) },
      };
    }
    return (await res.json()) as Envelope<T>;
  } catch (err: any) {
    return {
      ok: false,
      data: null,
      error: { message: err?.message || "network", code: "NETWORK" },
    };
  }
}

export const getFallenEdgeLatest = () =>
  feGet<FallenEdgePayload>("/fallen-edge/latest");

export const getFallenEdgeCandidates = () =>
  feGet<FallenEdgePayload>("/fallen-edge/candidates");

export const getFallenEdgeReversals = () =>
  feGet<FallenEdgePayload>("/fallen-edge/reversals");

export const getFallenEdgeShortContinuations = () =>
  feGet<FallenEdgePayload>("/fallen-edge/short-continuations");

export const getFallenEdgeAvoid = () =>
  feGet<FallenEdgePayload>("/fallen-edge/avoid");

export const getFallenEdgeHealth = () =>
  feGet<FallenEdgeHealth>("/fallen-edge/health");
